// AJOUTER UNE PIÈCE TERRAIN : une photo, une vidéo, la voix, ou une note,
// rangée dans l'étude en deux gestes.
//
// Photo et vidéo passent par le sélecteur du téléphone, qui propose lui-même
// l'appareil photo ou la galerie. La voix (enregistreur) n'est proposée qu'en
// mode mobile : c'est un geste de terrain.
import { useEffect, useRef, useState } from 'react';
import { ApiRequestError } from '../api/client';
import { Button, ErrorBanner, Field, Note } from '../components/ui';
import { Icon, type IconName } from '../components/icons';
import { useIsMobile } from '../components/device';
import { TextArea } from '../write/Forms';
import { useAddFieldItem, type FieldItem, type FieldKind } from './api';
import { shrinkPhoto } from './image';
import { VoiceRecorder } from './VoiceRecorder';
import '../interviews/interviews.css';
import './capture.css';

const KINDS: { value: FieldKind; label: string; icon: IconName; mobileOnly?: boolean; file?: boolean }[] = [
  { value: 'photo', label: 'Photo', icon: 'camera', file: true },
  { value: 'video', label: 'Vidéo', icon: 'video', file: true },
  { value: 'audio', label: 'Voix', icon: 'mic', mobileOnly: true, file: true },
  { value: 'note', label: 'Note', icon: 'text' },
];

function errorOf(cause: unknown): string {
  if (cause instanceof ApiRequestError || cause instanceof Error) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

export function FieldCapture({
  studyId,
  uploads,
  initialKind = 'photo',
  onDone,
}: {
  studyId: string;
  /** Le serveur reçoit-il des fichiers ? (non en production sans stockage) */
  uploads: boolean;
  initialKind?: FieldKind;
  onDone: (item: FieldItem) => void;
}) {
  const mobile = useIsMobile();
  const add = useAddFieldItem(studyId);
  const kinds = KINDS.filter((k) => (!k.mobileOnly || mobile) && (!k.file || uploads));
  const [kind, setKind] = useState<FieldKind>(kinds.some((k) => k.value === initialKind) ? initialKind : kinds[0]?.value ?? 'note');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [note, setNote] = useState('');
  const [consent, setConsent] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [preparing, setPreparing] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // Aperçu local de la photo ou de la vidéo choisie.
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  function choose(k: FieldKind) {
    setKind(k);
    setFile(null);
    add.reset();
  }

  async function pick(f: File | undefined) {
    if (!f) return;
    setPreparing(true);
    setFile(kind === 'photo' ? await shrinkPhoto(f) : f);
    setPreparing(false);
  }

  async function send(payload: { file?: File; noteText?: string; takenAt?: string; kind?: FieldKind }): Promise<boolean> {
    setProgress(payload.file ? 0 : null);
    try {
      const item = await add.mutateAsync({
        kind: payload.kind ?? kind,
        caption: caption.trim() || undefined,
        ...(payload.file ? { file: payload.file, consent: true, onProgress: setProgress } : {}),
        ...(payload.noteText ? { noteText: payload.noteText } : {}),
        ...(payload.takenAt ? { takenAt: payload.takenAt } : {}),
      });
      setFile(null);
      setCaption('');
      setNote('');
      onDone(item);
      return true;
    } catch {
      return false;
    } finally {
      setProgress(null);
      if (input.current) input.current.value = '';
    }
  }

  const fileKind = kind === 'photo' || kind === 'video';

  return (
    <div className="field-capture">
      <div className="itw-sources" role="radiogroup" aria-label="Type de pièce">
        {kinds.map((k) => (
          <button
            key={k.value}
            type="button"
            role="radio"
            aria-checked={kind === k.value}
            className={`itw-source${kind === k.value ? ' itw-on' : ''}`}
            onClick={() => choose(k.value)}
          >
            <Icon name={k.icon} size={18} />
            {k.label}
          </button>
        ))}
      </div>
      {!uploads ? (
        <Note tone="lecture">L’envoi de photos et de vidéos n’est pas encore ouvert sur ce serveur : seules les notes sont possibles.</Note>
      ) : null}

      {kind !== 'note' ? (
        <Field
          label="Légende"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder={kind === 'photo' ? 'Ce que montre la photo' : kind === 'video' ? 'Ce que montre la vidéo' : 'De quoi il s’agit'}
        />
      ) : null}

      {kind !== 'note' ? (
        <label className="terms-consent recording-consent">
          <input className="zy-check" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>Les personnes visibles ou enregistrées ont été informées et ont donné leur accord.</span>
        </label>
      ) : null}

      {fileKind ? (
        <>
          {/* Sans `capture` : le téléphone propose l'appareil photo OU la galerie. */}
          <input
            ref={input}
            type="file"
            accept={kind === 'photo' ? 'image/*' : 'video/*'}
            className="sr-only"
            onChange={(e) => void pick(e.target.files?.[0])}
          />
          {preview ? (
            kind === 'photo'
              ? <img className="field-preview" src={preview} alt="Aperçu de la photo choisie" />
              : <video className="field-preview" src={preview} controls preload="metadata" />
          ) : null}
          <div className="capture-or">
            <Button variant={file ? 'ghost' : 'accent'} onClick={() => input.current?.click()} loading={preparing} disabled={progress !== null}>
              <Icon name={kind === 'photo' ? 'camera' : 'video'} size={16} />
              {file ? 'Changer' : kind === 'photo' ? 'Prendre ou choisir une photo' : 'Filmer ou choisir une vidéo'}
            </Button>
            {file ? (
              <Button
                variant="accent"
                disabled={!consent}
                loading={progress !== null}
                onClick={() => void send({ file, takenAt: new Date(file.lastModified || Date.now()).toISOString() })}
              >
                Ajouter à l’étude
              </Button>
            ) : null}
          </div>
          {file && !consent ? <p className="zy-field__aide">Cochez l’accord des personnes pour ajouter.</p> : null}
        </>
      ) : null}

      {kind === 'audio' ? (
        <VoiceRecorder
          storageKey={`field:${studyId}`}
          disabled={!consent}
          busy={progress !== null}
          onSend={(f) => send({ file: f, kind: 'audio' })}
        />
      ) : null}

      {kind === 'note' ? (
        <>
          <TextArea label="Note" value={note} onChange={setNote} rows={5} placeholder="Ce que vous avez observé, entendu, remarqué…" />
          <Button variant="accent" disabled={!note.trim()} loading={add.isPending} onClick={() => void send({ noteText: note.trim() })}>
            Ajouter la note
          </Button>
        </>
      ) : null}

      {progress !== null ? (
        <div className="upload-progress">
          <progress className="zy-progress" value={Math.round(progress * 100)} max={100} aria-label="Envoi du fichier" />
          <em>{Math.round(progress * 100)} %</em>
        </div>
      ) : null}
      {add.error ? <ErrorBanner>{errorOf(add.error)}</ErrorBanner> : null}
    </div>
  );
}
